import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  ArrowLeft,
  Search,
  Factory,
  Heart,
  Landmark,
  Cpu,
  Headphones,
  Clock,
  Target,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { PROJECT_TEMPLATES, getTemplateById } from '../data/projectTemplates';
import { LogoIcon } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRY_ICONS = {
  manufacturing: Factory,
  healthcare: Heart,
  finance: Landmark,
  technology: Cpu,
  services: Headphones,
};

const INDUSTRY_COLORS = {
  manufacturing: 'bg-blue-500',
  healthcare: 'bg-red-500',
  finance: 'bg-green-500',
  technology: 'bg-purple-500',
  services: 'bg-amber-500',
};

export default function ProjectTemplates() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const filterTemplates = (templates) => {
    if (!searchQuery) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(
      (t) =>
        t.title.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.industry.toLowerCase().includes(query)
    );
  };

  const handleUseTemplate = async () => {
    if (!selectedTemplate) return;
    
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: selectedTemplate.title,
          description: selectedTemplate.description,
          industry: selectedTemplate.industry,
          problem_statement: selectedTemplate.defaultProblem,
          scope: selectedTemplate.suggestedScope,
          template_id: selectedTemplate.id,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create project');
      }

      const project = await response.json();
      toast.success('Project created from template!');
      navigate(`/project/${project.project_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const renderTemplateCard = (template, industry) => {
    const Icon = INDUSTRY_ICONS[industry] || FileText;
    const colorClass = INDUSTRY_COLORS[industry] || 'bg-slate-500';

    return (
      <Card
        key={template.id}
        className="bg-slate-800 border-slate-700 hover:border-cyan-500/50 transition-colors cursor-pointer"
        onClick={() => setSelectedTemplate(template)}
        data-testid={`template-${template.id}`}
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 ${colorClass} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-white font-medium truncate">{template.title}</h3>
              </div>
              <p className="text-sm text-slate-400 line-clamp-2 mb-3">
                {template.description}
              </p>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <Badge variant="outline" className="border-slate-600">
                  {template.industry}
                </Badge>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {template.typicalDuration}
                </span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="project-templates">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <LogoIcon className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <FileText className="w-6 h-6 text-cyan-400" />
              Project Templates
            </h1>
            <p className="text-slate-400 mt-1">
              Start with pre-configured templates for common improvement projects
            </p>
          </div>
        </div>
        <Link to="/dashboard">
          <Button variant="outline" className="border-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-slate-800 border-slate-700 text-white"
          data-testid="template-search"
        />
      </div>

      {/* Industry Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800 border-slate-700">
          <TabsTrigger value="all" className="data-[state=active]:bg-cyan-600">
            All Industries
          </TabsTrigger>
          <TabsTrigger value="manufacturing" className="data-[state=active]:bg-cyan-600">
            <Factory className="w-4 h-4 mr-1" />
            Manufacturing
          </TabsTrigger>
          <TabsTrigger value="healthcare" className="data-[state=active]:bg-cyan-600">
            <Heart className="w-4 h-4 mr-1" />
            Healthcare
          </TabsTrigger>
          <TabsTrigger value="finance" className="data-[state=active]:bg-cyan-600">
            <Landmark className="w-4 h-4 mr-1" />
            Finance
          </TabsTrigger>
          <TabsTrigger value="technology" className="data-[state=active]:bg-cyan-600">
            <Cpu className="w-4 h-4 mr-1" />
            Technology
          </TabsTrigger>
          <TabsTrigger value="services" className="data-[state=active]:bg-cyan-600">
            <Headphones className="w-4 h-4 mr-1" />
            Services
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {Object.entries(PROJECT_TEMPLATES).map(([industry, templates]) => {
            const filtered = filterTemplates(templates);
            if (filtered.length === 0) return null;
            return (
              <div key={industry}>
                <h3 className="text-lg font-medium text-white capitalize mb-3 flex items-center gap-2">
                  {React.createElement(INDUSTRY_ICONS[industry] || FileText, { className: 'w-5 h-5 text-cyan-400' })}
                  {industry}
                </h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((template) => renderTemplateCard(template, industry))}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {Object.entries(PROJECT_TEMPLATES).map(([industry, templates]) => (
          <TabsContent key={industry} value={industry} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filterTemplates(templates).map((template) => renderTemplateCard(template, industry))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Template Detail Dialog */}
      <Dialog open={!!selectedTemplate} onOpenChange={() => setSelectedTemplate(null)}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              {selectedTemplate?.title}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-300 mb-2">Problem Statement Template</h4>
                <p className="text-slate-400 text-sm">{selectedTemplate.defaultProblem}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Suggested Metrics</h4>
                  <ul className="space-y-1">
                    {selectedTemplate.suggestedMetrics?.map((metric, i) => (
                      <li key={i} className="text-sm text-slate-400 flex items-center gap-2">
                        <Target className="w-3 h-3 text-cyan-400" />
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-slate-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Common Root Causes</h4>
                  <ul className="space-y-1">
                    {selectedTemplate.commonRootCauses?.map((cause, i) => (
                      <li key={i} className="text-sm text-slate-400">• {cause}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Typical Duration: {selectedTemplate.typicalDuration}
                </span>
                <Badge variant="outline" className="border-slate-600">
                  {selectedTemplate.industry}
                </Badge>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedTemplate(null)}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUseTemplate}
              disabled={creating}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="use-template-btn"
            >
              {creating ? 'Creating...' : 'Use This Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
